import type { GetRef } from 'antd/es/_util/type';
import {
  default as Select,
  type SelectValue,
  type DefaultOptionType,
  type SelectProps
} from 'antd/es/select';
import { default as Tooltip } from 'antd/es/tooltip';
import { useMemo, useRef } from 'react';
import { CountryFlag } from 'shared/src/CountryFlag';
import { getCountryCode } from 'shared/src/getCountryCode';

import { filterOption } from 'utils/antd/filters-option';
import { virtualAccountNumberFormatter } from 'utils/formatters/virtualAccountNumberFormatter';

import { AccountStatus } from 'api';

import type { VirtualAccount } from 'models/virtualAccount';

import { useVirtualAccounts } from 'hooks/context';

import { Spinner } from '@atoms/Spinner/Spinner';

const { Option } = Select;

export const virtualAccountLabel = (item: VirtualAccount) =>
  item.name || item.currency_name;

type Props = {
  onChangeCallback:
    | ((
        value: string,
        option: DefaultOptionType | DefaultOptionType[] | undefined
      ) => void)
    | undefined;
  e2eId?: string;
  accounts?: VirtualAccount[];
  allowAll?: boolean;
  currentAccountNumber?: string | undefined;
  filterUnownedAccounts?: boolean;
  filterTerminatedAccounts?: boolean;
  filterSuspendedAccounts?: boolean;
  filterCryptoAccounts?: boolean;
  /** @deprecated This property is no longer used - simply remove it */
  organisationId?: string | number;
  isWarning?: boolean;
  style?: React.CSSProperties;
} & SelectProps<string>;

/**
 * ANT select that shows the user's virtual accounts
 */
export function SelectVirtualAccount({
  onChangeCallback = () => {},
  currentAccountNumber,
  accounts,
  e2eId,
  style,
  defaultValue,
  isWarning = false,
  allowAll = true,
  filterUnownedAccounts = false,
  filterTerminatedAccounts = false,
  filterSuspendedAccounts = false,
  filterCryptoAccounts = false,
  ...props
}: Props) {
  const { accounts: fetchedAccounts, isLoading } = useVirtualAccounts({
    filterUnownedAccounts,
    filterTerminatedAccounts,
    filterSuspendedAccounts,
    filterCryptoAccounts
  });

  const selectRef = useRef<GetRef<typeof Select>>(null);

  // Allow for custom data instead of fetching
  const accountList = useMemo(() => {
    if (Boolean(accounts)) return accounts;
    return fetchedAccounts;
  }, [accounts, fetchedAccounts]);

  return (
    <Select<string>
      showSearch
      placeholder="Select an account"
      value={currentAccountNumber}
      data-testid={e2eId}
      status={isWarning ? 'warning' : ''}
      getPopupContainer={(trigger) => trigger.parentNode}
      notFoundContent={
        !isLoading && (
          <div className="flex justify-center py-2">
            <Spinner />
          </div>
        )
      }
      className="mini-cool-shadow w-[160px]"
      style={{ ...style }}
      popupMatchSelectWidth={false}
      defaultValue={allowAll && !defaultValue ? 'All' : defaultValue}
      filterOption={filterOption}
      labelRender={(node) => (
        <AccountLabelRender value={node.value} accountOptions={accountList} />
      )}
      onChange={(...data) => {
        // Remove focus from selector, when an account has been selected
        selectRef.current?.blur();
        // Handle onChange callback
        onChangeCallback(...data);
      }}
      ref={selectRef}
      {...props}
    >
      {allowAll && (
        <Option key="All" index="All">
          All Accounts
        </Option>
      )}
      {accountList?.map((account) => {
        const isShared = account.owner === false;
        const isSuspended = account.status === AccountStatus.suspended;
        const isTerminated = account.status === AccountStatus.terminated;

        return (
          <Option
            index={`${account.account_number} ${account.name}`}
            key={account.account_number}
          >
            <div className="flex flex-col text-sm">
              <div className="flex flex-row items-center gap-2">
                <CountryFlag
                  className="!size-[22px]"
                  countryCode={getCountryCode(account.currency_code)}
                  svg
                />
                <div className="flex flex-col overflow-hidden">
                  <div className="flex min-w-0 flex-row items-center gap-1">
                    <Tooltip title={virtualAccountLabel(account)}>
                      <span className="truncate">
                        {virtualAccountLabel(account)}
                      </span>
                    </Tooltip>
                    {isShared && (
                      <span className="bold shrink-0 pl-2 text-xs font-bold text-highlight-primary">
                        SHARED
                      </span>
                    )}
                    {isSuspended && (
                      <span className="bold shrink-0 pl-2 text-xs font-bold text-feedback-attention">
                        SUSPENDED
                      </span>
                    )}
                    {isTerminated && (
                      <span className="bold shrink-0 pl-2 text-xs font-bold text-content-secondary">
                        TERMINATED
                      </span>
                    )}
                  </div>
                  <div className="text-[.8rem] text-content-secondary">
                    No. {virtualAccountNumberFormatter(account.account_number)}
                  </div>
                </div>
              </div>
            </div>
          </Option>
        );
      })}
    </Select>
  );
}

const AccountLabelRender = ({
  value,
  accountOptions
}: {
  value: SelectValue;
  accountOptions?: VirtualAccount[];
}) => {
  if (value === 'All') {
    return <p>All Accounts</p>;
  }

  const account = accountOptions?.find(
    (account) => account.account_number === value
  );

  return account ? (
    <div className="flex flex-row items-center gap-2">
      <CountryFlag
        className="!size-[22px]"
        countryCode={getCountryCode(account.currency_code)}
        svg
      />

      <p data-testid="account-label" className="truncate">
        {virtualAccountLabel(account)}
      </p>
    </div>
  ) : null;
};
